import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as echarts from 'echarts';

interface Contractor {
  id: string;
  name: string;
  category: string;
  totalEntries: number;
  violationsCount: number;
  violationSeverity: number; // 1-10 嚴重度評分
  safetyScore: number; // 0-100 安全評分
  complianceRate: number; // 0-100% 合規率
  responseTime: number; // 平均回應時間(小時)
  personnelCount: number; // 登記人員數量
  workCompletionRate: number; // 0-100% 工作完成率
  onTimeRate: number; // 0-100% 準時率
  avgStayHours: number; // 平均停留時間
}

@Component({
  selector: 'app-contractor-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contractor-report.component.html',
  styleUrl: './contractor-report.component.scss'
})
export class ContractorReportComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('violationChart') violationChartEl!: ElementRef;
  @ViewChild('entryChart') entryChartEl!: ElementRef;
  @ViewChild('safetyChart') safetyChartEl!: ElementRef;
  @ViewChild('complianceChart') complianceChartEl!: ElementRef;
  @ViewChild('performanceChart') performanceChartEl!: ElementRef;
  @ViewChild('trendChart') trendChartEl!: ElementRef;

  // 篩選選項
  dateRange = {
    start: this.getThreeMonthsAgo(),
    end: new Date()
  };
  selectedCategory: string = 'all';
  categories: string[] = ['all', '電力工程', '空調系統', '水電維修', '機械設備', '清潔服務', '安全系統'];

  // 報表數據
  contractors: Contractor[] = [];
  violationCategories: { [key: string]: number } = {};
  monthlyTrends: { month: string; violations: number; entries: number; safetyScore: number }[] = [];

  // 統計摘要
  totalViolations: number = 0;
  totalEntries: number = 0;
  avgSafetyScore: number = 0;
  avgComplianceRate: number = 0;

  // 圖表實例
  private violationChart: echarts.ECharts | null = null;
  private entryChart: echarts.ECharts | null = null;
  private safetyChart: echarts.ECharts | null = null;
  private complianceChart: echarts.ECharts | null = null;
  private performanceChart: echarts.ECharts | null = null;
  private trendChart: echarts.ECharts | null = null;
  private charts: echarts.ECharts[] = [];
  private resizeObserver: ResizeObserver | null = null;

  constructor() {}

  ngOnInit(): void {
    // 載入模擬數據
    this.loadMockData();
    // 計算統計摘要
    this.calculateSummary();
  }

  ngAfterViewInit(): void {
    // 創建圖表實例
    setTimeout(() => {
      this.initCharts();
      this.setupResizeObserver();
    });
  }

  ngOnDestroy(): void {
    // 銷毀圖表實例
    this.charts.forEach(chart => {
      if (chart) {
        chart.dispose();
      }
    });

    // 移除 ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  // 設置 ResizeObserver 監聽容器尺寸變化
  setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeCharts();
      });

      const chartContainers = [
        this.violationChartEl?.nativeElement,
        this.entryChartEl?.nativeElement,
        this.safetyChartEl?.nativeElement,
        this.complianceChartEl?.nativeElement,
        this.performanceChartEl?.nativeElement,
        this.trendChartEl?.nativeElement
      ];

      chartContainers.forEach(container => {
        if (container) {
          this.resizeObserver!.observe(container);
        }
      });
    } else {
      // 備用方案：使用 window resize 事件
      window.addEventListener('resize', this.resizeCharts.bind(this));
    }
  }

  // 調整圖表大小
  resizeCharts(): void {
    this.charts.forEach(chart => {
      if (chart) {
        chart.resize();
      }
    });
  }

  // 載入模擬數據
  loadMockData(): void {
    // 承包商資料
    this.contractors = [
      {
        id: 'C001',
        name: '宏達電力工程有限公司',
        category: '電力工程',
        totalEntries: 156,
        violationsCount: 3,
        violationSeverity: 4,
        safetyScore: 87,
        complianceRate: 93,
        responseTime: 2.4,
        personnelCount: 24,
        workCompletionRate: 98,
        onTimeRate: 92,
        avgStayHours: 6.5
      },
      {
        id: 'C002',
        name: '冠好冷氣空調工程',
        category: '空調系統',
        totalEntries: 89,
        violationsCount: 1,
        violationSeverity: 2,
        safetyScore: 95,
        complianceRate: 98,
        responseTime: 1.8,
        personnelCount: 12,
        workCompletionRate: 96,
        onTimeRate: 95,
        avgStayHours: 4.2
      },
      {
        id: 'C003',
        name: '永信水電工程有限公司',
        category: '水電維修',
        totalEntries: 120,
        violationsCount: 6,
        violationSeverity: 5,
        safetyScore: 78,
        complianceRate: 85,
        responseTime: 3.6,
        personnelCount: 18,
        workCompletionRate: 92,
        onTimeRate: 86,
        avgStayHours: 5.8
      },
      {
        id: 'C004',
        name: '盛源機械工程股份有限公司',
        category: '機械設備',
        totalEntries: 72,
        violationsCount: 2,
        violationSeverity: 7,
        safetyScore: 83,
        complianceRate: 90,
        responseTime: 4.2,
        personnelCount: 15,
        workCompletionRate: 94,
        onTimeRate: 88,
        avgStayHours: 7.3
      },
      {
        id: 'C005',
        name: '潔淨清潔服務公司',
        category: '清潔服務',
        totalEntries: 210,
        violationsCount: 4,
        violationSeverity: 3,
        safetyScore: 82,
        complianceRate: 88,
        responseTime: 2.1,
        personnelCount: 32,
        workCompletionRate: 97,
        onTimeRate: 96,
        avgStayHours: 3.2
      },
      {
        id: 'C006',
        name: '安全守護系統整合公司',
        category: '安全系統',
        totalEntries: 65,
        violationsCount: 0,
        violationSeverity: 0,
        safetyScore: 98,
        complianceRate: 99,
        responseTime: 1.3,
        personnelCount: 9,
        workCompletionRate: 99,
        onTimeRate: 97,
        avgStayHours: 5.1
      },
      {
        id: 'C007',
        name: '振隆電力工程股份有限公司',
        category: '電力工程',
        totalEntries: 132,
        violationsCount: 5,
        violationSeverity: 6,
        safetyScore: 75,
        complianceRate: 82,
        responseTime: 3.8,
        personnelCount: 27,
        workCompletionRate: 91,
        onTimeRate: 84,
        avgStayHours: 6.9
      },
      {
        id: 'C008',
        name: '耀陽空調冷凍設備有限公司',
        category: '空調系統',
        totalEntries: 98,
        violationsCount: 2,
        violationSeverity: 3,
        safetyScore: 89,
        complianceRate: 94,
        responseTime: 2.5,
        personnelCount: 16,
        workCompletionRate: 95,
        onTimeRate: 93,
        avgStayHours: 4.8
      },
    ];

    // 違規類別統計
    this.violationCategories = {
      '安全裝備不符規定': 8,
      '未經授權進入禁區': 5,
      '工作許可證過期': 3,
      '設備操作不當': 4,
      '工作區域未清理': 7,
      '超時工作': 2,
      '未按規定報到離場': 6
    };

    // 月度趨勢數據
    const months = ['一月', '二月', '三月', '四月', '五月'];
    this.monthlyTrends = months.map((month, index) => {
      return {
        month,
        violations: Math.floor(Math.random() * 8) + 1,
        entries: 80 + Math.floor(Math.random() * 120),
        safetyScore: 75 + Math.floor(Math.random() * 25)
      };
    });
  }

  // 計算統計摘要
  calculateSummary(): void {
    const filteredContractors = this.getFilteredContractors();

    this.totalViolations = filteredContractors.reduce((sum, c) => sum + c.violationsCount, 0);
    this.totalEntries = filteredContractors.reduce((sum, c) => sum + c.totalEntries, 0);

    const sumSafety = filteredContractors.reduce((sum, c) => sum + c.safetyScore, 0);
    this.avgSafetyScore = filteredContractors.length ? +(sumSafety / filteredContractors.length).toFixed(1) : 0;

    const sumCompliance = filteredContractors.reduce((sum, c) => sum + c.complianceRate, 0);
    this.avgComplianceRate = filteredContractors.length ? +(sumCompliance / filteredContractors.length).toFixed(1) : 0;
  }

  // 獲取過濾後的承包商數據
  getFilteredContractors(): Contractor[] {
    return this.contractors.filter(c =>
      (this.selectedCategory === 'all' || c.category === this.selectedCategory)
    );
  }

  // 初始化所有圖表
  initCharts(): void {
    this.initViolationChart();
    this.initEntryChart();
    this.initSafetyChart();
    this.initComplianceChart();
    this.initPerformanceChart();
    this.initTrendChart();
  }

  // 初始化違規排行榜圖表
  initViolationChart(): void {
    if (!this.violationChartEl) return;

    const chartDom = this.violationChartEl.nativeElement;
    this.violationChart = echarts.init(chartDom);
    this.charts.push(this.violationChart);

    const sortedContractors = [...this.getFilteredContractors()]
      .sort((a, b) => b.violationsCount - a.violationsCount)
      .slice(0, 5);

    const option: echarts.EChartsOption = {
      title: {
        text: '承攬商違規排行榜',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['違規次數', '違規嚴重度'],
        top: 'bottom'
      },
      xAxis: {
        type: 'category',
        data: sortedContractors.map(c => this.shortenName(c.name)),
        axisLabel: {
          interval: 0,
          rotate: 30
        }
      },
      yAxis: {
        type: 'value',
        name: '次數/嚴重度'
      },
      series: [
        {
          name: '違規次數',
          type: 'bar',
          data: sortedContractors.map(c => c.violationsCount),
          itemStyle: {
            color: 'rgba(255, 99, 132, 0.7)'
          }
        },
        {
          name: '違規嚴重度',
          type: 'bar',
          data: sortedContractors.map(c => c.violationSeverity),
          itemStyle: {
            color: 'rgba(54, 162, 235, 0.7)'
          }
        }
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      }
    };

    this.violationChart.setOption(option);
  }

  // 初始化進廠次數圖表
  initEntryChart(): void {
    if (!this.entryChartEl) return;

    const chartDom = this.entryChartEl.nativeElement;
    this.entryChart = echarts.init(chartDom);
    this.charts.push(this.entryChart);

    const sortedContractors = [...this.getFilteredContractors()]
      .sort((a, b) => b.totalEntries - a.totalEntries)
      .slice(0, 5);

    const option: echarts.EChartsOption = {
      title: {
        text: '承攬商進廠次數統計',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['進廠次數', '登記人員數量'],
        top: 'bottom'
      },
      xAxis: {
        type: 'category',
        data: sortedContractors.map(c => this.shortenName(c.name)),
        axisLabel: {
          interval: 0,
          rotate: 30
        }
      },
      yAxis: {
        type: 'value',
        name: '次數'
      },
      series: [
        {
          name: '進廠次數',
          type: 'bar',
          data: sortedContractors.map(c => c.totalEntries),
          itemStyle: {
            color: 'rgba(75, 192, 192, 0.7)'
          }
        },
        {
          name: '登記人員數量',
          type: 'bar',
          data: sortedContractors.map(c => c.personnelCount),
          itemStyle: {
            color: 'rgba(153, 102, 255, 0.7)'
          }
        }
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      }
    };

    this.entryChart.setOption(option);
  }

  // 初始化安全評分圖表
  initSafetyChart(): void {
    if (!this.safetyChartEl) return;

    const chartDom = this.safetyChartEl.nativeElement;
    this.safetyChart = echarts.init(chartDom);
    this.charts.push(this.safetyChart);

    const sortedContractors = [...this.getFilteredContractors()]
      .sort((a, b) => b.safetyScore - a.safetyScore);

    const option: echarts.EChartsOption = {
      title: {
        text: '承攬商安全評分',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: '{b}: {c}'
      },
      xAxis: {
        type: 'value',
        name: '評分 (0-100)',
        max: 100,
        min: 0
      },
      yAxis: {
        type: 'category',
        data: sortedContractors.map(c => this.shortenName(c.name)),
        inverse: true
      },
      series: [
        {
          name: '安全評分',
          type: 'bar',
          data: sortedContractors.map(c => ({
            value: c.safetyScore,
            itemStyle: {
              color: this.getColorByScore(c.safetyScore)
            }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: '{c}'
          }
        }
      ],
      grid: {
        left: '3%',
        right: '10%',
        bottom: '3%',
        top: '15%',
        containLabel: true
      }
    };

    this.safetyChart.setOption(option);
  }

  // 初始化合規率圖表
  initComplianceChart(): void {
    if (!this.complianceChartEl) return;

    const chartDom = this.complianceChartEl.nativeElement;
    this.complianceChart = echarts.init(chartDom);
    this.charts.push(this.complianceChart);

    const data = this.getFilteredContractors().map(c => ({
      name: this.shortenName(c.name),
      value: c.complianceRate
    }));

    const option: echarts.EChartsOption = {
      title: {
        text: '承攬商合規率比較',
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c}%'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        formatter: function(name: string) {
          const item = data.find(d => d.name === name);
          return `${name} (${item ? item.value : 0}%)`;
        }
      },
      series: [
        {
          name: '合規率',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
              formatter: '{b}: {c}%'
            }
          },
          labelLine: {
            show: false
          },
          data: data,
          center: ['40%', '50%']
        }
      ]
    };

    this.complianceChart.setOption(option);
  }

  // 初始化績效雷達圖
  initPerformanceChart(): void {
    if (!this.performanceChartEl) return;

    const chartDom = this.performanceChartEl.nativeElement;
    this.performanceChart = echarts.init(chartDom);
    this.charts.push(this.performanceChart);

    // 選擇前3名進行比較
    const topContractors = [...this.getFilteredContractors()]
      .sort((a, b) =>
        (b.safetyScore + b.complianceRate + b.workCompletionRate + b.onTimeRate) / 4 -
        (a.safetyScore + a.complianceRate + a.workCompletionRate + a.onTimeRate) / 4
      )
      .slice(0, 3);

    const option: echarts.EChartsOption = {
      title: {
        text: '承攬商綜合績效比較',
        left: 'center'
      },
      tooltip: {
        trigger: 'item'
      },
      legend: {
        data: topContractors.map(c => this.shortenName(c.name)),
        bottom: '0%'
      },
      radar: {
        indicator: [
          { name: '安全評分', max: 100 },
          { name: '合規率', max: 100 },
          { name: '工作完成率', max: 100 },
          { name: '準時率', max: 100 },
          { name: '回應速度', max: 100 }
        ],
        center: ['50%', '55%'],
        radius: '65%'
      },
      series: [{
        type: 'radar',
        data: topContractors.map((c, index) => ({
          name: this.shortenName(c.name),
          value: [
            c.safetyScore,
            c.complianceRate,
            c.workCompletionRate,
            c.onTimeRate,
            100 - (c.responseTime * 10) // 將回應時間轉換為0-100分
          ],
          areaStyle: {
            opacity: 0.2,
            color: this.getDatasetColor(index, 0.5)
          },
          lineStyle: {
            width: 2,
            color: this.getDatasetColor(index, 1)
          },
          itemStyle: {
            color: this.getDatasetColor(index, 1)
          }
        }))
      }]
    };

    this.performanceChart.setOption(option);
  }

  // 初始化趨勢圖表
  initTrendChart(): void {
    if (!this.trendChartEl) return;

    const chartDom = this.trendChartEl.nativeElement;
    this.trendChart = echarts.init(chartDom);
    this.charts.push(this.trendChart);

    const option: echarts.EChartsOption = {
      title: {
        text: '月度趨勢分析',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        }
      },
      legend: {
        data: ['違規次數', '進廠次數', '平均安全評分'],
        bottom: '0%'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: this.monthlyTrends.map(t => t.month)
      },
      yAxis: [
        {
          type: 'value',
          name: '違規次數',
          position: 'left',
          min: 0,
          axisLabel: {
            formatter: '{value}'
          }
        },
        {
          type: 'value',
          name: '進廠次數',
          position: 'right',
          min: 0,
          axisLabel: {
            formatter: '{value}'
          }
        },
        {
          type: 'value',
          name: '安全評分',
          min: 0,
          max: 100,
          position: 'right',
          offset: 80,
          axisLabel: {
            formatter: '{value}'
          }
        }
      ],
      series: [
        {
          name: '違規次數',
          type: 'line',
          yAxisIndex: 0,
          data: this.monthlyTrends.map(t => t.violations),
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgba(255, 99, 132, 1)'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(255, 99, 132, 0.5)' },
                { offset: 1, color: 'rgba(255, 99, 132, 0)' }
              ]
            }
          },
          smooth: true
        },
        {
          name: '進廠次數',
          type: 'line',
          yAxisIndex: 1,
          data: this.monthlyTrends.map(t => t.entries),
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgba(54, 162, 235, 1)'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(54, 162, 235, 0.5)' },
                { offset: 1, color: 'rgba(54, 162, 235, 0)' }
              ]
            }
          },
          smooth: true
        },
        {
          name: '平均安全評分',
          type: 'line',
          yAxisIndex: 2,
          data: this.monthlyTrends.map(t => t.safetyScore),
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgba(75, 192, 192, 1)'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(75, 192, 192, 0.5)' },
                { offset: 1, color: 'rgba(75, 192, 192, 0)' }
              ]
            }
          },
          smooth: true
        }
      ]
    };

    this.trendChart.setOption(option);
  }

  // 取得違規類別資料
  getViolationCategories(): { name: string, count: number }[] {
    return Object.entries(this.violationCategories)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  // 獲取最大違規次數
  getMaxViolationCount(): number {
    const counts = Object.values(this.violationCategories);
    return counts.length ? Math.max(...counts) : 0;
  }

  // 獲取安全評分的樣式類別
  getSafetyScoreClass(score: number): string {
    if (score >= 90) return 'score-excellent';
    if (score >= 80) return 'score-good';
    if (score >= 70) return 'score-average';
    return 'score-poor';
  }

  // 更新圖表數據
  updateCharts(): void {
    this.calculateSummary();

    // 銷毀現有圖表
    this.charts.forEach(chart => {
      if (chart) {
        chart.dispose();
      }
    });
    this.charts = [];

    // 重新初始化圖表
    this.initCharts();
  }

  // 更新過濾條件
  updateFilter(): void {
    this.updateCharts();
  }

  // 獲取三個月前的日期
  getThreeMonthsAgo(): Date {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date;
  }

  // 縮短名稱以適應圖表顯示
  shortenName(name: string): string {
    if (name.length <= 8) return name;
    return name.substring(0, 7) + '...';
  }

  // 根據評分獲取顏色
  getColorByScore(score: number): string {
    if (score >= 90) return 'rgba(75, 192, 192, 0.7)'; // 優秀 - 青藍色
    if (score >= 80) return 'rgba(54, 162, 235, 0.7)'; // 良好 - 藍色
    if (score >= 70) return 'rgba(255, 206, 86, 0.7)'; // 中等 - 黃色
    if (score >= 60) return 'rgba(255, 159, 64, 0.7)'; // 及格 - 橙色
    return 'rgba(255, 99, 132, 0.7)'; // 不及格 - 紅色
  }

  // 獲取數據集顏色
  getDatasetColor(index: number, alpha: number): string {
    const colors = [
      `rgba(54, 162, 235, ${alpha})`,
      `rgba(255, 99, 132, ${alpha})`,
      `rgba(75, 192, 192, ${alpha})`,
      `rgba(255, 206, 86, ${alpha})`,
      `rgba(153, 102, 255, ${alpha})`,
      `rgba(255, 159, 64, ${alpha})`
    ];
    return colors[index % colors.length];
  }
}
